import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [registerData, setRegisterData] = useState({
    tenantName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    zip: '',
    taxSystem: 'DE'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [demoCredentials, setDemoCredentials] = useState(null);
  const [showDemoBox, setShowDemoBox] = useState(false);

  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      showNotification('E-Mail-Adresse erfolgreich bestätigt! Sie können sich jetzt anmelden.', 'success');
    }
    
    if (searchParams.get('demo') === 'true') {
      setShowDemoBox(true);
    }
    
    const usernameParam = searchParams.get('username');
    const passwordParam = searchParams.get('password');
    if (usernameParam && passwordParam) {
      setFormData({ username: usernameParam, password: passwordParam });
      handleDemoLogin(usernameParam, passwordParam);
    }
    
    loadDemoCredentials();
  }, [searchParams, showNotification]);

  const loadDemoCredentials = async () => {
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/demo-credentials`);
      if (response.ok) {
        const data = await response.json();
        setDemoCredentials(data);
      }
    } catch (error) {
      console.log('Demo credentials not available');
    }
  };

  const handleDemoLogin = async (username, password) => {
    setFormData({ username, password });
    setError('');
    setAttemptsRemaining(null);
    setLockedUntil(null);
    setLoading(true);

    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        let errorMessage = 'Anmeldung fehlgeschlagen';
        let errorData = {};
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `Server-Fehler: ${response.status} ${response.statusText}`;
        }
        
        if (errorData.locked) {
          setLockedUntil(errorData.remainingMinutes);
          setError(errorMessage);
        } else {
          setError(errorMessage);
          if (errorData.attemptsRemaining !== undefined) {
            setAttemptsRemaining(errorData.attemptsRemaining);
          }
        }
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.token || !data.user) {
        setError('Ungültige Antwort vom Server');
        setLoading(false);
        return;
      }

      setAttemptsRemaining(null);
      setLockedUntil(null);
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      if (data.user.role === 'admin') {
        localStorage.removeItem('tenantId');
      } else if (data.user.tenantId) {
        localStorage.setItem('tenantId', data.user.tenantId);
      }
      
      setTimeout(() => {
        if (data.passwordChangeRequired) {
          window.location.href = '/change-password';
        } else if (data.user.role === 'admin') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie, ob der Server läuft.');
      } else {
        setError(error.message || 'Anmeldung fehlgeschlagen');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAttemptsRemaining(null);
    setLockedUntil(null);
    setLoading(true);

    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let errorMessage = 'Anmeldung fehlgeschlagen';
        let errorData = {};
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = `Server-Fehler: ${response.status} ${response.statusText}`;
        }
        
        if (errorData.locked) {
          setLockedUntil(errorData.remainingMinutes);
          setError(errorMessage);
        } else {
          setError(errorMessage);
          if (errorData.attemptsRemaining !== undefined) {
            setAttemptsRemaining(errorData.attemptsRemaining);
          }
        }
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (!data.token || !data.user) {
        setError('Ungültige Antwort vom Server');
        setLoading(false);
        return;
      }

      setAttemptsRemaining(null);
      setLockedUntil(null);
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      if (data.user.role === 'admin') {
        localStorage.removeItem('tenantId');
      } else if (data.user.tenantId) {
        localStorage.setItem('tenantId', data.user.tenantId);
      }
      
      setTimeout(() => {
        if (data.passwordChangeRequired) {
          window.location.href = '/change-password';
        } else if (data.user.role === 'admin') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie, ob der Server läuft.');
      } else {
        setError(error.message || 'Anmeldung fehlgeschlagen');
      }
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: formData.username
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Versenden der E-Mail');
      }

      const data = await response.json();
      showNotification(data.message || 'Bestätigungsmail wurde erneut versendet. Bitte prüfen Sie Ihr Postfach.', 'success');
      setEmailNotVerified(false);
    } catch (error) {
      showNotification(error.message || 'Fehler beim Versenden der E-Mail. Bitte versuchen Sie es erneut.', 'error');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (registerData.password !== registerData.confirmPassword) {
      setError('Die Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    if (registerData.password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantName: registerData.tenantName,
          email: registerData.email,
          username: registerData.username,
          password: registerData.password,
          firstName: registerData.firstName,
          lastName: registerData.lastName,
          address: {
            street: registerData.street,
            city: registerData.city,
            zip: registerData.zip,
            country: 'Deutschland'
          },
          taxSystem: registerData.taxSystem
        })
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `Server-Fehler: ${response.status} ${response.statusText}` };
        }
        setError(errorData.error || errorData.details || 'Registrierung fehlgeschlagen');
        setLoading(false);
        return;
      }

      const data = await response.json();
      showNotification('Registrierung erfolgreich! Bitte prüfen Sie Ihr E-Mail-Postfach zur Bestätigung.', 'success');
      setMode('login');
      setRegisterData({
        tenantName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        zip: '',
        taxSystem: 'DE'
      });
    } catch (error) {
      console.error('Registration error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const apiUrl = config.API_URL || 'https://api.garagedesk.net/api';
        setError(`Verbindungsfehler: Kann nicht mit dem Server verbinden (${apiUrl}). Bitte prüfen Sie Ihre Internetverbindung oder ob der Server läuft.`);
      } else {
        setError(error.message || 'Fehler bei der Registrierung. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-content-wrapper">
        <div className="login-wrapper">
          <div className="login-box">
        <div className="login-header">
          <div className="logo-section">
            <h1 className="logo">
              <span style={{ color: '#333' }}>Garage</span>
              <span style={{ color: '#ff9800' }}>Desk</span>
            </h1>
            <div className="beta-badge">
              <span className="beta-label">BETA</span>
              <span className="beta-text">Diese Anwendung befindet sich in der Beta-Phase und kann Fehler enthalten.</span>
            </div>
          </div>
        </div>

        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => {
              setMode('login');
              setError('');
            }}
          >
            Anmelden
          </button>
          <button
            type="button"
            className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
            onClick={() => {
              setMode('register');
              setError('');
            }}
          >
            Registrieren
          </button>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label>Benutzername</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Passwort</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            {error && (
              <div className={`error-message ${lockedUntil ? 'error-locked' : ''} ${emailNotVerified ? 'error-email-not-verified' : ''}`}>
                {error}
                {attemptsRemaining !== null && attemptsRemaining > 0 && (
                  <div className="attempts-warning">
                    Verbleibende Versuche: {attemptsRemaining}
                  </div>
                )}
                {emailNotVerified && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingEmail}
                      className="btn-secondary"
                      style={{ width: '100%', marginTop: '0.5rem' }}
                    >
                      {resendingEmail ? (
                        <>
                          <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-envelope" style={{ marginRight: '0.5rem' }}></i>
                          Bestätigungsmail erneut versenden
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading || lockedUntil !== null}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                  Anmelden...
                </>
              ) : lockedUntil ? (
                <>
                  <i className="fas fa-lock" style={{ marginRight: '0.5rem' }}></i>
                  Gesperrt ({lockedUntil} Min)
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt" style={{ marginRight: '0.5rem' }}></i>
                  Anmelden
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="login-form register-form">
            <div className="form-row">
              <div className="form-group">
                <label>Werkstattname *</label>
                <input
                  type="text"
                  value={registerData.tenantName}
                  onChange={(e) => setRegisterData({ ...registerData, tenantName: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>E-Mail-Adresse *</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Vorname</label>
                <input
                  type="text"
                  value={registerData.firstName}
                  onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Nachname</label>
                <input
                  type="text"
                  value={registerData.lastName}
                  onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Benutzername *</label>
                <input
                  type="text"
                  value={registerData.username}
                  onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Steuersystem *</label>
                <select
                  value={registerData.taxSystem}
                  onChange={(e) => setRegisterData({ ...registerData, taxSystem: e.target.value })}
                  required
                >
                  <option value="DE">Deutschland</option>
                  <option value="AT">Österreich</option>
                  <option value="CH">Schweiz</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Straße</label>
              <input
                type="text"
                value={registerData.street}
                onChange={(e) => setRegisterData({ ...registerData, street: e.target.value })}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>PLZ</label>
                <input
                  type="text"
                  value={registerData.zip}
                  onChange={(e) => setRegisterData({ ...registerData, zip: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Stadt</label>
                <input
                  type="text"
                  value={registerData.city}
                  onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Passwort *</label>
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen *</label>
                <input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  required
                />
              </div>
            </div>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                  Registrieren...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus" style={{ marginRight: '0.5rem' }}></i>
                  Registrieren
                </>
              )}
            </button>
            <p className="register-hint">
              Nach der Registrierung erhalten Sie eine E-Mail zur Bestätigung Ihrer E-Mail-Adresse.
            </p>
          </form>
        )}
        </div>

        {showDemoBox && demoCredentials && (
          <div className="demo-credentials-box">
            <h3>Demo-Zugang</h3>
            <div className="demo-warning" style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '15px',
              fontSize: '13px',
              color: '#856404'
            }}>
              ⚠️ Dieser Demo-Account wird alle 24 Stunden automatisch zurückgesetzt.
            </div>
            <p>
              Testen Sie GarageDesk mit vorgefertigten Demo-Daten:
            </p>
            {demoCredentials.credentials.map((cred, index) => (
              <div key={index} className="demo-credential-item" onClick={() => handleDemoLogin(cred.username, cred.password)}>
                <div className="role">
                  {cred.userRole || cred.role}
                </div>
                <div className="credentials">
                  <div>Benutzer: <strong>{cred.username}</strong></div>
                  <div>Passwort: <strong>{cred.password}</strong></div>
                </div>
              </div>
            ))}
            <div className="demo-credentials-hint">
              Klicken Sie auf einen Benutzer zum automatischen Einloggen
            </div>
          </div>
        )}
        </div>
        
        <footer className="login-footer">
        <div className="login-footer-content">
          <button 
            type="button"
            className="login-footer-link demo-link"
            onClick={() => {
              if (demoCredentials) {
                setShowDemoBox(!showDemoBox);
              } else {
                loadDemoCredentials().then(() => {
                  setShowDemoBox(true);
                });
              }
            }}
          >
            Werkstatt Demo
          </button>
          <Link 
            to="/customer-login?email=testkunde@example.com&password=test123"
            className="login-footer-link demo-link"
          >
            Kunde Demo
          </Link>
          <Link to="/support" className="login-footer-link">
            Support
          </Link>
          <Link to="/impressum" className="login-footer-link">
            Impressum
          </Link>
          <Link to="/documentation" className="login-footer-link">
            Dokumentation
          </Link>
          <Link to="/api-documentation" className="login-footer-link">
            API
          </Link>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default Login;

